import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  BriefcaseIcon,
  PhoneIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { employeeApi } from '../../api/employeeApi';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await employeeApi.getEmployees();
      setEmployees(res.data.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await employeeApi.deleteEmployee(id);
      toast.success("Employee deleted successfully");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to delete employee");
    }
  };

  const handleDownloadIDCard = async (e, id, name) => {
    e.stopPropagation();
    const loadingToast = toast.loading('Generating ID Card...');
    try {
      const res = await employeeApi.downloadIDCard(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ID_Card_${name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('ID Card downloaded!');
    } catch (error) {
      toast.error('Failed to generate ID Card');
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (emp.phone && emp.phone.includes(searchQuery))
  );

  return (
    <div className="w-full pb-20 max-w-full md:px-4 mx-auto space-y-6 pt-2">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            Staff & Payroll
          </h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">
            Manage your employees, attendance, salaries, and advances.
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/employees/add')}
            className="flex items-center gap-2 bg-[#dc343b] text-white px-5 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Employee
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-lg mt-6">
        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] transition-shadow text-[15px] font-medium shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]"
          placeholder="Search employees by name or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Employee List Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
        {loading ? (
          <div className="col-span-full py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#dc343b] mx-auto"></div>
          </div>
        ) : filteredEmployees.length > 0 ? (
          filteredEmployees.map(emp => (
            <div 
              key={emp._id}
              onClick={() => navigate(`/employees/${emp._id}`)}
              className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-xl cursor-pointer transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
            >
              {/* Status Indicator */}
              <div className="absolute top-6 right-6 flex items-center gap-1.5">
                <span className={`flex w-2.5 h-2.5 rounded-full ${emp.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{emp.status}</span>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-gray-100 dark:border-gray-700 shrink-0 shadow-sm">
                  {emp.profilePicture ? (
                    <img src={emp.profilePicture} alt={emp.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center text-[#dc343b] dark:text-red-400 font-extrabold text-2xl">
                      {emp.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-extrabold text-[17px] text-gray-900 dark:text-white group-hover:text-[#dc343b] transition-colors">
                    {emp.name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-[13px] text-gray-500 dark:text-gray-400 mt-1">
                    <BriefcaseIcon className="w-4 h-4" />
                    <span>{emp.role}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                {emp.phone && (
                  <div className="flex items-center gap-2 text-[13px] text-gray-600 dark:text-gray-300">
                    <PhoneIcon className="w-4 h-4 text-gray-400" />
                    <span>{emp.phone}</span>
                  </div>
                )}
                
                <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-2xl p-4 border border-gray-100/50 dark:border-gray-700/50">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-[12px] text-gray-500 font-bold uppercase tracking-wider">Base Salary</span>
                    <span className="text-[14px] font-bold text-gray-700 dark:text-gray-300">
                      ₹{emp.salaryAmount?.toLocaleString() || 0} <span className="text-[11px] text-gray-500 font-normal">/{emp.salaryType === 'daily' ? 'day' : 'mo'}</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-gray-500 font-bold uppercase tracking-wider">Pending/Adv.</span>
                    <span className={`text-[15px] font-extrabold ${emp.currentBalance >= 0 ? 'text-red-500' : 'text-green-600'}`}>
                      ₹{Math.abs(emp.currentBalance || 0).toLocaleString()}
                      <span className="text-[10px] ml-1">{emp.currentBalance < 0 ? '(Adv)' : ''}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Overlay */}
              <div className="absolute top-0 right-0 bottom-0 left-0 bg-[#dc343b]/90 dark:bg-[#b91c1c]/90 flex flex-col items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-[2px]">
                <div className="flex items-center gap-4">
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate(`/employees/edit/${emp._id}`); }}
                    className="bg-white text-[#dc343b] p-3 rounded-full hover:scale-110 transition-transform shadow-lg"
                    title="Edit Profile"
                  >
                    <PencilSquareIcon className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={(e) => handleDelete(e, emp._id)}
                    className="bg-gray-900 text-white p-3 rounded-full hover:scale-110 transition-transform shadow-lg"
                    title="Delete Employee"
                  >
                    <TrashIcon className="w-6 h-6" />
                  </button>
                </div>
                <button 
                  onClick={(e) => handleDownloadIDCard(e, emp._id, emp.name)}
                  className="bg-white text-[#dc343b] px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-red-50 transition-colors shadow-lg font-bold text-[13px]"
                >
                  <IdentificationIcon className="w-5 h-5" />
                  Download ID Card
                </button>
              </div>

            </div>
          ))
        ) : (
          <div className="col-span-full py-24 text-center bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-300 dark:border-gray-700">
            <div className="bg-red-50 dark:bg-gray-700 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserGroupIcon className="h-10 w-10 text-[#dc343b] dark:text-red-400" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 dark:text-white mb-2">No Team Members Found</h3>
            <p className="text-gray-500 text-[15px] mb-8 max-w-md mx-auto">
              Start building your team by adding your first employee. Track their attendance and manage payroll easily.
            </p>
            <button 
              onClick={() => navigate('/employees/add')}
              className="bg-[#dc343b] text-white px-8 py-3.5 rounded-2xl font-bold text-[15px] hover:bg-[#b91c1c] transition-colors shadow-lg shadow-red-500/30"
            >
              Add First Employee
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Employees;
